import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { MdCloudSync, MdAdd, MdPlayArrow, MdHistory, MdDelete, MdEdit, MdInfoOutline, MdCategory, MdSchedule } from 'react-icons/md';

import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import MainButton from '../../../shared/layout/buttons/MainButton';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import EmptyState from '../../../shared/layout/feedback/EmptyState';
import StandardModal from '../../../shared/layout/modals/StandardModal';
import { StyledInput, StyledLabel, FormGroup } from '../../../shared/layout/forms/FormElements';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import SchemaForm from './SchemaForm';
import {
    createCatalogSource,
    detectCatalogFeed,
    discoverCatalogCategories,
    deleteCatalogSource,
    fetchCatalogSourceRuns,
    fetchCatalogSourceRunErrors,
    fetchCatalogSources,
    fetchSyncAdapters,
    syncCatalogSource,
    suggestCatalogFieldMapping,
    testCatalogSource,
    updateCatalogSource,
} from '../../../services/Http/Api';

/** Supported target fields. At least one stable source identity is required; EAN is optional. */
const TARGET_FIELDS = [
    { key: 'ean' },
    { key: 'external_id' },
    { key: 'parent_external_id' },
    { key: 'external_variant_id' },
    { key: 'sku' },
    { key: 'product_name' },
    { key: 'manufacturer_name' },
    { key: 'category_name' },
    { key: 'price' },
    { key: 'sale_price' },
    { key: 'discount_percentage' },
    { key: 'discount_value' },
    { key: 'discount_start_at' },
    { key: 'discount_end_at' },
    { key: 'currency' },
    { key: 'stock' },
    { key: 'stock_status' },
    { key: 'url' },
    { key: 'image_url' },
    { key: 'warranty_months' },
];
const IDENTITY_FIELDS = ['external_id', 'sku', 'ean', 'gtin', 'upc'];
const hasIdentityMapping = (mapping) => IDENTITY_FIELDS.some((field) => mapping[field])
    || (mapping.parent_external_id && mapping.external_variant_id);

/**
 * Predloži mapiranje iz naziva polja u feedu.
 *
 * Feedovi u regiji dolaze i na hrvatskom i na engleskom, pa pogodak štedi
 * trgovcu pet padajućih izbornika. Prijedlog se uvijek može promijeniti —
 * nijedno polje se ne mapira mimo njegove potvrde jer je već prikazano u formi.
 */
const FIELD_HINTS = {
    ean: ['ean', 'barcode', 'gtin', 'crtni_kod', 'barkod'],
    external_id: ['external_product_id', 'external_id', 'product_id', 'id', 'article_id'],
    parent_external_id: ['parent_external_id', 'parent_id', 'product_id', 'id'],
    external_variant_id: ['external_variant_id', 'variant_id', 'variation_id', 'id'],
    sku: ['sku', 'sifra', 'šifra', 'article', 'artikl'],
    product_name: ['product_name', 'name', 'naziv', 'title'],
    manufacturer_name: ['manufacturer_name', 'manufacturer', 'brand', 'proizvodjac', 'proizvođač'],
    category_name: ['category_name', 'category', 'kategorija'],
    price: ['price', 'cijena', 'cena', 'mpc', 'amount'],
    sale_price: ['sale_price', 'discounted_price', 'akcijska_cijena', 'snizena_cijena'],
    discount_percentage: ['discount_percentage', 'discount_percent', 'popust_postotak'],
    discount_value: ['discount_value', 'popust', 'iznos_popusta'],
    discount_start_at: ['discount_start_at', 'discount_started_at', 'akcija_od'],
    discount_end_at: ['discount_end_at', 'discount_ended_at', 'akcija_do'],
    currency: ['currency', 'valuta'],
    stock: ['stock', 'quantity', 'kolicina', 'količina', 'zaliha', 'qty'],
    stock_status: ['stock_status', 'availability', 'stocktext', 'availability_text', 'stock_text', 'dostupnost', 'stanje'],
    url: ['url', 'link', 'product_url', 'poveznica'],
    image_url: ['image_url', 'image', 'slika'],
    warranty_months: ['warranty_months', 'warranty', 'jamstvo_mjeseci', 'garancija_mjeseci'],
};

const suggestMapping = (fields, current) => {
    const next = { ...current };

    TARGET_FIELDS.forEach(({ key }) => {
        if (next[key]) return;

        const hit = fields.find((field) => {
            const sourceField = String(field).toLowerCase();
            const leaf = sourceField.split('.').pop().split(':').pop();
            if (key === 'parent_external_id' && !sourceField.startsWith('parent.')) return false;
            if (key === 'external_variant_id' && !sourceField.startsWith('variant.')) return false;
            if (key === 'external_id' && /^(parent|variant)\./.test(sourceField)) return false;
            return FIELD_HINTS[key].includes(leaf);
        });
        if (hit) next[key] = hit;
    });

    return next;
};

const OUTCOME_KEYS = [
    'created', 'price_updated', 'stock_updated', 'url_updated',
    'discount_created', 'discount_updated', 'delisted', 'reactivated', 'unchanged',
    'to_staging', 'no_ean', 'skipped_over_limit', 'filtered',
];

/**
 * Izvori kataloga trgovca.
 *
 * Čarobnjak namjerno traži test konekcije prije spremanja: bez uzorka trgovac
 * spoji krivi feed, čeka dan i dobije prazan katalog bez objašnjenja.
 */
const CatalogSources = () => {
    const { t } = useTranslation();

    const [sources, setSources] = useState([]);
    const [limits, setLimits] = useState(null);
    const [adapters, setAdapters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const [wizardOpen, setWizardOpen] = useState(false);
    const [advanced, setAdvanced] = useState(false);
    const [adapterCode, setAdapterCode] = useState('');
    const [name, setName] = useState('');
    const [config, setConfig] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
    const [frequency, setFrequency] = useState(1);
    const [sample, setSample] = useState(null);
    const [testing, setTesting] = useState(false);
    const [savingSource, setSavingSource] = useState(false);
    const [suggestingMapping, setSuggestingMapping] = useState(false);
    const [mapping, setMapping] = useState({});
    const [mappingSuggestions, setMappingSuggestions] = useState({});
    const [editingSource, setEditingSource] = useState(null);
    const [runAt, setRunAt] = useState('');

    const [runsFor, setRunsFor] = useState(null);
    const [runs, setRuns] = useState([]);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([fetchCatalogSources(), fetchSyncAdapters()])
            .then(([list, adapterList]) => {
                setSources(list?.data ?? []);
                setLimits(list?.limits ?? null);
                setAdapters(adapterList ?? []);
                setError(null);
            })
            .catch(() => setError(t('CatalogSources.loadFailed')))
            .finally(() => setLoading(false));
    }, [t]);

    useEffect(load, [load]);
    useEffect(() => {
        const timer = setInterval(() => {
            if (document.hidden || wizardOpen) return;
            fetchCatalogSources().then(list => setSources(list?.data ?? [])).catch(() => {});
        }, 10000);
        return () => clearInterval(timer);
    }, [wizardOpen]);

    const selectedAdapter = adapters.find((a) => a.code === adapterCode) ?? null;
    const openWizard = () => {
        const first = adapters[0]?.code ?? '';
        setAdapterCode(first);
        setName('');
        setConfig({});
        setFieldErrors({});
        setFrequency(1);
        setSample(null);
        setMapping({});
        setMappingSuggestions({});
        setEditingSource(null);
        setRunAt('');
        setWizardOpen(true);
        setAdvanced(false);
    };

    const openEdit = (source) => {
        setAdvanced(true);
        setEditingSource(source);
        setAdapterCode(source.adapter_code);
        setName(source.name || '');
        setConfig(source.config || {});
        setFieldErrors({});
        setFrequency(source.syncs_per_day || 1);
        setRunAt((source.run_at || []).join(', '));
        setMapping(source.mapping || {});
        setMappingSuggestions({});
        setSample({
            ok: true,
            total_detected: source.last_items_total || 0,
            detected_fields: Array.from(new Set(Object.values(source.mapping || {}))),
            items: [],
        });
        setWizardOpen(true);
    };

    const runTest = async () => {
        setTesting(true);
        setFieldErrors({});
        try {
            const detected = !editingSource && !advanced
                ? await detectCatalogFeed(config.feed_url) : { adapter_code: adapterCode, config };
            setAdapterCode(detected.adapter_code);
            setConfig(detected.config);
            if (!name && detected.config.feed_url) {
                const parsed = new URL(detected.config.feed_url);
                setName(`${parsed.hostname}${parsed.pathname}`.slice(0, 255));
            }
            const result = await testCatalogSource({
                ...detected,
                ...(editingSource ? { source_id: editingSource.id } : {}),
            });
            setSample(result);
            setMapping((current) => suggestMapping(result?.detected_fields || [], current));
        } catch (err) {
            setSample(null);
            toast.error(err?.response?.data?.error || t('CatalogSources.testFailed'));
        } finally {
            setTesting(false);
        }
    };

    const suggestMappingWithAi = async () => {
        if (!sample?.ok) return;
        setSuggestingMapping(true);
        try {
            const result = await suggestCatalogFieldMapping(sample.detected_fields || [], sample.items || []);
            const proposed = {};
            Object.entries(result.suggestions || {}).forEach(([target, suggestion]) => {
                if (suggestion?.source_field) proposed[target] = suggestion.source_field;
            });
            setMappingSuggestions(result.suggestions || {});
            setMapping((current) => ({ ...current, ...proposed }));
            toast.success(t('CatalogSources.aiMappingReady', {
                defaultValue: `Prijedlog mapiranja je spreman (${Object.keys(proposed).length} polja). Pregledaj ga prije spremanja.`,
            }));
        } catch (err) {
            toast.error(err?.response?.data?.error || t('CatalogSources.aiMappingFailed', { defaultValue: 'AI prijedlog mapiranja nije uspio.' }));
        } finally {
            setSuggestingMapping(false);
        }
    };

    const save = async () => {
        if (savingSource) return;
        const payload = {
            adapter_code: adapterCode,
            name: name || selectedAdapter?.name || t('CatalogSources.defaultName'),
            config,
            syncs_per_day: frequency,
            run_at: runAt.split(',').map((value) => value.trim()).filter(Boolean),
            mapping,
            ...(!editingSource ? { active: false, auto_process_enriched: true } : {}),
        };
        setSavingSource(true);
        try {
            if (editingSource) {
                await updateCatalogSource(editingSource.id, { ...payload, adapter_code: undefined });
            } else {
                await createCatalogSource(payload);
            }
            toast.success(t(editingSource ? 'CatalogSources.updated' : 'CatalogSources.created'));
            setWizardOpen(false);
            load();
        } catch (err) {
            const response = err?.response?.data;
            if (response?.errors) {
                setFieldErrors(response.errors);
                toast.error(response.message || t('CatalogSources.invalidConfig'));
            } else {
                toast.error(t('CatalogSources.saveFailed'));
            }
        } finally {
            setSavingSource(false);
        }
    };

    const runNow = (source) => {
        setBusyId(source.id);
        (source.active ? Promise.resolve() : updateCatalogSource(source.id, { active: true }))
            .then(() => syncCatalogSource(source.id))
            .then((result) => {
                toast.success(t('CatalogSources.syncQueued', {
                    used: result?.used,
                    allowed: result?.allowed,
                }));
                load();
            })
            .catch((err) => {
                if (err?.response?.status === 429) {
                    const data = err.response.data;
                    toast.error(t('CatalogSources.quotaSpent', { used: data.used, allowed: data.allowed }));
                } else {
                    toast.error(err?.response?.data?.error || t('CatalogSources.syncFailed'));
                }
            })
            .finally(() => setBusyId(null));
    };

    const setSyncEnabled = async (source, enabled) => {
        setBusyId(source.id);
        try {
            await updateCatalogSource(source.id, { active: enabled });
            toast.success(enabled
                ? t('CatalogSources.dailyEnabled', { defaultValue: 'Dnevni sync je uključen. Prvi sync je stavljen u red.' })
                : t('CatalogSources.dailyDisabled', { defaultValue: 'Dnevni sync je pauziran.' }));
            load();
        } catch (err) {
            toast.error(err?.response?.data?.error || t('CatalogSources.saveFailed'));
        } finally {
            setBusyId(null);
        }
    };

    const remove = (source) => {
        if (!window.confirm(t('CatalogSources.confirmDelete', { name: source.name }))) return;

        deleteCatalogSource(source.id)
            .then(() => { toast.success(t('CatalogSources.deleted')); load(); })
            .catch(() => toast.error(t('CatalogSources.deleteFailed')));
    };

    const openRuns = (source) => {
        setRunsFor(source);
        fetchCatalogSourceRuns(source.id)
            .then(async (result) => {
                const runList = result?.data ?? [];
                const enriched = await Promise.all(runList.map(async (run) => {
                    if (!run.errors_count) return run;
                    try {
                        const errors = await fetchCatalogSourceRunErrors(source.id, run.id);
                        return { ...run, error_summary: errors?.by_reason || {} };
                    } catch (_) {
                        return run;
                    }
                }));
                setRuns(enriched);
            })
            .catch(() => toast.error(t('CatalogSources.runsFailed')));
    };

    if (loading) return <LoadingMessage />;
    if (error) return <ErrorMessage>{error}</ErrorMessage>;

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdCloudSync size={28} />}>{t('CatalogSources.title')}</PageTitle>
                <MainButton type="button" onClick={openWizard} disabled={adapters.length === 0}>
                    <MdAdd /> {t('CatalogSources.add')}
                </MainButton>
            </PageHeader>

            <PageContent>
                {limits && (
                    <LimitsNote>
                        <MdInfoOutline aria-hidden="true" />
                        {t('CatalogSources.limitsNote', {
                            perDay: limits.sync_per_day ?? '—',
                            used: limits.syncs_used_today ?? 0,
                            products: limits.products ?? '—',
                        })}
                    </LimitsNote>
                )}

                {sources.length === 0 ? (
                    <EmptyState>{t('CatalogSources.empty')}</EmptyState>
                ) : (
                    sources.map((source) => (
                        <SourceCard key={source.id}>
                            <SourceHeader>
                                <SourceIdentity>
                                    <SourceIcon aria-hidden="true"><MdCloudSync /></SourceIcon>
                                    <div>
                                        <SourceName>{source.name}</SourceName>
                                        <SourceMeta>
                                            {source.adapter_name || source.adapter_code}
                                            <MetaDot>·</MetaDot>
                                            {t('CatalogSources.perDay', { n: source.syncs_per_day })}
                                        </SourceMeta>
                                    </div>
                                </SourceIdentity>

                                <StatusBox>
                                    <StatusBadge $status={source.last_status}>
                                        <StatusDot $status={source.last_status} />
                                        {source.last_status
                                            ? t(`CatalogSources.status_${source.last_status}`)
                                            : t('CatalogSources.status_never')}
                                    </StatusBadge>
                                    <LastRun>
                                        {source.last_run_at
                                            ? <><strong>{new Date(source.last_run_at).toLocaleString('hr-HR')}</strong><span>{source.last_items_total ?? 0} {t('CatalogSources.items')}</span></>
                                            : t('CatalogSources.waitingFirstRun')}
                                    </LastRun>
                                </StatusBox>
                            </SourceHeader>

                            {!source.active && <DraftNote>{t('CatalogSources.draft', { defaultValue: 'Dnevni sync je pauziran. Možeš ga uključiti ispod ili pokrenuti ručno.' })}</DraftNote>}
                            {source.last_error && <ErrorNote>{source.last_error}</ErrorNote>}

                            <Actions>
                                {!source.managed_by_plugin && (!source.managed_by_system || source.supports_category_discovery) && <GhostButton disabled={busyId === source.id} onClick={async () => {
                                    setBusyId(source.id);
                                    try {
                                        const result = await discoverCatalogCategories(source.id);
                                        toast.success(t('CatalogSources.categoriesRead', { defaultValue: 'Kategorije su očitane. Otvorite pregled kategorija.' }));
                                        if (!result.counts?.category_rows) toast.info(t('CatalogSources.noCategories', { defaultValue: 'Nema kategorija. Provjerite mapiranje polja kategorije.' }));
                                    } catch (err) { toast.error(err?.response?.data?.error || t('CatalogSources.testFailed')); }
                                    finally { setBusyId(null); }
                                }}><MdCategory />{t('CatalogSources.readCategories', { defaultValue: 'Očitaj kategorije' })}</GhostButton>}
                                <ActionLink to={`/catalog-import?view=categories${source.managed_by_plugin ? '' : `&source=${encodeURIComponent(source.name.toLowerCase())}`}`}><MdCategory />{t('CatalogSources.reviewCategories', { defaultValue: 'Mapiranja kategorija' })}</ActionLink>
                                {source.managed_by_plugin || source.managed_by_system ? (
                                    <ManagedBadge>{source.adapter_code?.startsWith('reference_')
                                        ? t('CatalogSources.managedCommand', { defaultValue: 'Izvorom upravlja konfigurirani importer' })
                                        : source.supports_manual_sync
                                            ? t('CatalogSources.managedCommand', { defaultValue: 'Izvorom upravlja konfigurirani importer' })
                                            : t('CatalogSources.managedByPlugin')}</ManagedBadge>
                                ) : (
                                    <>
                                        <MainButton type="button" disabled={busyId === source.id} onClick={() => runNow(source)}>
                                            <MdPlayArrow /> {t('CatalogSources.syncNow')}
                                        </MainButton>
                                        <GhostButton type="button" onClick={() => openEdit(source)}>
                                            <MdEdit /> {t('CatalogSources.edit')}
                                        </GhostButton>
                                    </>
                                )}
                                {source.managed_by_system && source.supports_manual_sync && <MainButton type="button" disabled={busyId === source.id} onClick={() => runNow(source)}>
                                    <MdPlayArrow /> {t('CatalogSources.syncNow')}
                                </MainButton>}
                                <GhostButton type="button" onClick={() => openRuns(source)}>
                                    <MdHistory /> {t('CatalogSources.history')}
                                </GhostButton>
                                {!source.managed_by_plugin && !source.managed_by_system && (
                                    <GhostButton type="button" onClick={() => remove(source)}>
                                        <MdDelete /> {t('CatalogSources.delete')}
                                    </GhostButton>
                                )}
                            </Actions>
                            {!source.managed_by_plugin && <ScheduleRow>
                                <ScheduleLabel>
                                <input type="checkbox" aria-label={t('CatalogSources.dailyEnabledLabel', { defaultValue: 'Dnevni sync uključen' })}
                                    checked={Boolean(source.active)} disabled={busyId === source.id}
                                    onChange={(event) => setSyncEnabled(source, event.target.checked)} />
                                    <span>{t('CatalogSources.dailyEnabledLabel', { defaultValue: 'Dnevni sync uključen' })}</span>
                                </ScheduleLabel>
                                <ScheduleMeta>
                                    <MdSchedule aria-hidden="true" />
                                    {source.next_run_at && source.active
                                        ? <>{t('CatalogSources.nextRun', { defaultValue: 'Sljedeći sync' })}: {new Date(source.next_run_at).toLocaleString('hr-HR')}</>
                                        : t(source.active ? 'CatalogSources.scheduleActive' : 'CatalogSources.schedulePaused', { defaultValue: source.active ? 'Automatsko osvježavanje je uključeno' : 'Automatsko osvježavanje je isključeno' })}
                                </ScheduleMeta>
                            </ScheduleRow>}
                            <ProgressSection aria-label={t('CatalogSources.progress', { defaultValue: 'Napredak obrade kataloga' })}>
                                <ProgressHeading>
                                    <div>
                                        <h4>{t('CatalogSources.progress', { defaultValue: 'Napredak kataloga' })}</h4>
                                        <p>{t('CatalogSources.progressHint', { defaultValue: 'Stvarni broj proizvoda po statusu; ažurira se svakih 10 sekundi.' })}</p>
                                    </div>
                                    <RefreshHint>{t('CatalogSources.live', { defaultValue: 'Uživo · 10 s' })}</RefreshHint>
                                </ProgressHeading>
                                <ProgressGrid>
                                    {Object.entries({ published: 'U katalogu', waiting: 'Čeka obradu', queued: 'U redu', running: 'Obrađuje se', review: 'Za pregled', failed: 'Neuspjelo' }).map(([key, label]) =>
                                        <ProgressItem key={key} $state={key}>
                                            <dt>{t(`CatalogSources.progressLabels.${key}`, { defaultValue: label })}</dt>
                                            <dd>{source.progress?.[key] ?? '—'}</dd>
                                        </ProgressItem>
                                    )}
                                </ProgressGrid>
                            </ProgressSection>
                        </SourceCard>
                    ))
                )}
            </PageContent>

            {wizardOpen && (
                <StandardModal isOpen title={t(editingSource ? 'CatalogSources.edit' : 'CatalogSources.add')} onClose={() => setWizardOpen(false)}>
                    <p>{t('CatalogSources.flow', { defaultValue: '1. Očitaj izvor → 2. Potvrdi polja → 3. Spremi i pregledaj kategorije → 4. Pokreni sinkronizaciju' })}</p>
                    <Help>{t('CatalogSources.publishHint', { defaultValue: 'Nakon pokretanja sinkronizacije, provjereni proizvodi automatski se objavljuju. Nejasni rezultati ostaju za pregled.' })}</Help>
                    {!advanced && <FormGroup>
                        <StyledLabel htmlFor="catalog-feed-url">URL kataloga (CSV / XML / JSON)</StyledLabel>
                        <StyledInput id="catalog-feed-url" type="url" value={config.feed_url || ''} disabled={testing}
                            onChange={e => { setConfig({ feed_url: e.target.value }); setSample(null); setMapping({}); }} />
                        <Help>{t('CatalogSources.detectHint', { defaultValue: 'Format prepoznajemo iz sadržaja. Unesite direktni URL kataloga, ne početnu stranicu trgovine.' })}</Help>
                    </FormGroup>}
                    <GhostButton type="button" disabled={testing} onClick={() => { setAdvanced(!advanced); setSample(null); }}>
                        {t('CatalogSources.advanced', { defaultValue: 'Napredne postavke / ručni odabir formata' })}
                    </GhostButton>
                    {advanced && <>
                    <FormGroup>
                        <StyledLabel>{t('CatalogSources.adapter')}</StyledLabel>
                        <SimpleSelect
                            value={adapterCode}
                            disabled={Boolean(editingSource)}
                            onChange={(e) => { setAdapterCode(e.target.value); setConfig({}); setSample(null); }}
                        >
                            {adapters.map((adapter) => (
                                <option key={adapter.code} value={adapter.code}>{adapter.name}</option>
                            ))}
                        </SimpleSelect>
                        {selectedAdapter?.description && <Help>{selectedAdapter.description}</Help>}
                    </FormGroup>

                    <SchemaForm
                        schema={selectedAdapter?.config_schema}
                        values={config}
                        errors={fieldErrors}
                        onChange={(next) => { setConfig(next); setSample(null); }}
                    />
                    </>}
                    <FormGroup>
                        <StyledLabel htmlFor="catalog-source-name">{t('CatalogSources.name')}</StyledLabel>
                        <StyledInput id="catalog-source-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={selectedAdapter?.name} />
                    </FormGroup>
                    <FormGroup>
                        <StyledLabel htmlFor="catalog-source-time">{t('CatalogSources.runAt')}</StyledLabel>
                        <StyledInput id="catalog-source-time" type="time" value={runAt.split(',')[0] || ''} onChange={(e) => setRunAt(e.target.value)} />
                        <Help>{t('CatalogSources.runAtHint')}</Help>
                    </FormGroup>

                    <Help>{t('CatalogSources.frequencyHint', { max: 1 })}</Help>

                    <TestRow>
                        <GhostButton type="button" onClick={runTest} disabled={testing}>
                            {testing ? t('CatalogSources.testing') : t('CatalogSources.test')}
                        </GhostButton>
                        {sample?.ok && (
                            <TestOk>
                                {t('CatalogSources.testOk', { total: sample.total_detected })}
                            </TestOk>
                        )}
                    </TestRow>

                    {sample?.ok && (
                        <SampleBox>
                            <SampleTitle>{t('CatalogSources.detectedFields')}</SampleTitle>
                            <FieldList>{(sample.detected_fields || []).join(', ')}</FieldList>

                            <SampleTitle>{t('CatalogSources.sample')}</SampleTitle>
                            <SampleTable>
                                <tbody>
                                    {(sample.items || []).slice(0, 5).map((item, index) => (
                                        <tr key={index}>
                                            {Object.values(item).slice(0, 4).map((value, i) => (
                                                <td key={i}>{String(value).slice(0, 40)}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </SampleTable>
                        </SampleBox>
                    )}

                    {sample?.ok && (
                        <MappingBox>
                            <SampleTitle>{t('CatalogSources.mapping')}</SampleTitle>
                            <Help>{t('CatalogSources.mappingHint')}</Help>
                            <GhostButton type="button" onClick={suggestMappingWithAi} disabled={suggestingMapping}>
                                {suggestingMapping ? t('CatalogSources.aiMappingRunning', { defaultValue: 'AI analizira polja…' }) : t('CatalogSources.aiMappingAction', { defaultValue: 'Predloži mapiranje pomoću AI-ja' })}
                            </GhostButton>

                            {TARGET_FIELDS.map((target) => (
                                <MappingRow key={target.key}>
                                    <MappingLabel>
                                        {t(`CatalogSources.field_${target.key}`)}
                                        {IDENTITY_FIELDS.includes(target.key) && <Required>*</Required>}
                                    </MappingLabel>
                                    <SimpleSelect
                                        value={mapping[target.key] || ''}
                                        onChange={(e) => setMapping({ ...mapping, [target.key]: e.target.value })}
                                    >
                                        <option value="">{t('CatalogSources.notMapped')}</option>
                                        {(sample.detected_fields || []).map((field) => (
                                            <option key={field} value={field}>{field}</option>
                                        ))}
                                    </SimpleSelect>
                                    {mappingSuggestions[target.key] && (
                                        <Help>
                                            {`AI ${Math.round((mappingSuggestions[target.key].confidence || 0) * 100)}% · ${mappingSuggestions[target.key].reason || ''}`}
                                        </Help>
                                    )}
                                </MappingRow>
                            ))}

                            {!hasIdentityMapping(mapping) && (
                                <MappingWarning>{t('CatalogSources.identityRequired', { defaultValue: 'Mapiraj barem jedan stabilan ID, SKU ili GTIN/EAN/UPC. Proizvodi bez bar-koda mogu se obraditi kada imaju stabilan izvorni ID ili SKU.' })}</MappingWarning>
                            )}
                        </MappingBox>
                    )}

                    <ModalActions>
                        <GhostButton type="button" onClick={() => setWizardOpen(false)}>
                            {t('CatalogSources.cancel')}
                        </GhostButton>
                        <MainButton type="button" onClick={save} disabled={savingSource || !sample?.ok || !hasIdentityMapping(mapping)}>
                            {savingSource ? t('CatalogSources.saving', { defaultValue: 'Spremanje…' }) : t('CatalogSources.save')}
                        </MainButton>
                    </ModalActions>
                    {!sample?.ok && <Help>{t('CatalogSources.testFirst')}</Help>}
                </StandardModal>
            )}

            {runsFor && (
                <StandardModal isOpen title={`${t('CatalogSources.history')}: ${runsFor.name}`} onClose={() => setRunsFor(null)}>
                    {runs.length === 0 ? (
                        <EmptyState>{t('CatalogSources.noRuns')}</EmptyState>
                    ) : (
                        runs.map((run) => (
                            <RunRow key={run.id}>
                                <RunHead>
                                    <StatusBadge $status={run.status}>{t(`CatalogSources.status_${run.status}`)}</StatusBadge>
                                    <span>{new Date(run.started_at).toLocaleString('hr-HR')}</span>
                                    <span>{Math.round((run.duration_ms || 0) / 1000)}s</span>
                                    <span>{run.items_seen} {t('CatalogSources.items')}</span>
                                </RunHead>

                                {Object.keys(run.outcome || {}).length > 0 && (
                                    <OutcomeList>
                                        {OUTCOME_KEYS.filter((key) => run.outcome[key]).map((key) => (
                                            <li key={key}>
                                                {t(`CatalogSources.outcome_${key}`)}: <strong>{run.outcome[key]}</strong>
                                            </li>
                                        ))}
                                    </OutcomeList>
                                )}

                                {run.error && <ErrorNote>{run.error}</ErrorNote>}
                                {Object.keys(run.error_summary || {}).length > 0 && (
                                    <ErrorNote>
                                        {Object.entries(run.error_summary).map(([reason, count]) => `${reason}: ${count}`).join(' · ')}
                                    </ErrorNote>
                                )}
                            </RunRow>
                        ))
                    )}
                </StandardModal>
            )}
        </PageContainer>
    );
};

const LimitsNote = styled.p`
    display: flex;
    align-items: center;
    gap: 0.55rem;
    margin: 0 0 1rem;
    padding: 0.75rem 0.9rem;
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.backgroundSecondary || '#f8fafb'};
    color: ${({ theme }) => theme.colors.textSecondary || theme.colors.textMuted || '#66727d'};
    font-size: 0.84rem;

    svg {
        flex: 0 0 auto;
        color: ${({ theme }) => theme.colors.brand || '#701a82'};
    }
`;

const SourceCard = styled(StyledCard)`
    margin-bottom: 1rem;
    padding: 1.25rem;
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    box-shadow: 0 2px 8px rgba(16, 24, 40, 0.04);
    border-radius: 10px;
`;

const SourceHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    flex-wrap: wrap;
`;

const SourceIdentity = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    min-width: 0;
`;

const SourceIcon = styled.span`
    display: grid;
    place-items: center;
    flex: 0 0 2.7rem;
    width: 2.7rem;
    height: 2.7rem;
    border-radius: 9px;
    background: ${({ theme }) => theme.colors.brandLighter || 'rgba(130, 22, 146, 0.1)'};
    color: ${({ theme }) => theme.colors.brand || '#701a82'};
    font-size: 1.35rem;
`;

const SourceName = styled.h3`
    margin: 0;
    color: ${({ theme }) => theme.colors.textPrimary || '#111827'};
    font-size: 1.05rem;
    font-weight: 650;
    line-height: 1.3;
    overflow-wrap: anywhere;
`;

const SourceMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
    margin-top: 0.3rem;
    color: ${({ theme }) => theme.colors.textMuted || '#66727d'};
    font-size: 0.8rem;
`;

const MetaDot = styled.span`
    color: #a0a8ae;
`;

const StatusBox = styled.div`
    text-align: right;
    min-width: 170px;
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.3rem 0.6rem;
    border-radius: 999px;
    color: ${({ $status }) => ['failed', 'timeout'].includes($status) ? '#a52828' : $status === 'partial' ? '#805800' : $status === 'ok' ? '#176b57' : '#59656f'};
    background: ${({ $status }) => ['failed', 'timeout'].includes($status) ? '#fdeceb' : $status === 'partial' ? '#fff4d6' : $status === 'ok' ? '#e6f4ee' : '#f0f2f4'};
    font-size: 0.76rem;
    font-weight: 650;
`;

const StatusDot = styled.span`
    width: 0.42rem;
    height: 0.42rem;
    border-radius: 50%;
    background: ${({ $status }) => ['failed', 'timeout'].includes($status) ? '#c0392b' : $status === 'partial' ? '#c28a16' : $status === 'ok' ? '#23825c' : '#89939b'};
`;

const LastRun = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    margin-top: 0.35rem;
    color: ${({ theme }) => theme.colors.textMuted || '#66727d'};
    font-size: 0.76rem;

    strong {
        color: ${({ theme }) => theme.colors.textSecondary || '#475467'};
        font-weight: 550;
    }
`;

const DraftNote = styled.p`
    margin: 0.9rem 0 0;
    padding: 0.65rem 0.8rem;
    border-left: 3px solid #d3a33a;
    border-radius: 4px;
    background: #fff9e9;
    color: #765610;
    font-size: 0.82rem;
    line-height: 1.45;
`;

const ErrorNote = styled.div`
    margin-top: 0.75rem;
    padding: 0.65rem 0.8rem;
    border: 1px solid #f3c9c6;
    border-radius: 6px;
    background: #fff5f4;
    color: #a52828;
    font-size: 0.82rem;
    line-height: 1.45;
    overflow-wrap: anywhere;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.55rem;
    margin-top: 1rem;
    flex-wrap: wrap;
`;

const ActionLink = styled(Link)`
    min-height: 36px;
    padding: 0.45rem 0.75rem;
    border: 1px solid ${({ theme }) => theme.colors.border || '#d7dde2'};
    border-radius: 6px;
    background: #fff;
    color: ${({ theme }) => theme.colors.textSecondary || '#40515b'};
    cursor: pointer;
    font-size: 0.84rem;
    font-weight: 550;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;

    &:hover {
        border-color: ${({ theme }) => theme.colors.brand || '#701a82'};
        color: ${({ theme }) => theme.colors.brand || '#701a82'};
    }
`;

const GhostButton = styled.button`
    min-height: 36px;
    background: #fff;
    border: 1px solid ${({ theme }) => theme.colors.border || '#d7dde2'};
    border-radius: 6px;
    padding: 0.45rem 0.75rem;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.textSecondary || '#40515b'};
    font-size: 0.84rem;
    font-weight: 550;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;

    &:hover:not(:disabled) {
        border-color: ${({ theme }) => theme.colors.brand || '#701a82'};
        color: ${({ theme }) => theme.colors.brand || '#701a82'};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ScheduleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-top: 0.85rem;
    padding: 0.75rem 0;
    border-top: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
`;

const ScheduleLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    color: ${({ theme }) => theme.colors.textSecondary || '#344054'};
    font-size: 0.84rem;
    font-weight: 600;
    cursor: pointer;

    input {
        width: 1rem;
        height: 1rem;
        margin: 0;
        accent-color: ${({ theme }) => theme.colors.brand || '#701a82'};
    }
`;

const ScheduleMeta = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: ${({ theme }) => theme.colors.textMuted || '#66727d'};
    font-size: 0.8rem;

    svg {
        color: ${({ theme }) => theme.colors.brand || '#701a82'};
    }
`;

const ProgressSection = styled.section`
    margin-top: 0.9rem;
    padding-top: 0.85rem;
`;

const ProgressHeading = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.7rem;

    h4 {
        margin: 0;
        color: ${({ theme }) => theme.colors.textPrimary || '#111827'};
        font-size: 0.9rem;
        font-weight: 650;
    }

    p {
        margin: 0.2rem 0 0;
        color: ${({ theme }) => theme.colors.textMuted || '#66727d'};
        font-size: 0.76rem;
        line-height: 1.4;
    }
`;

const RefreshHint = styled.span`
    flex: 0 0 auto;
    padding: 0.22rem 0.45rem;
    border-radius: 999px;
    background: #f0f2f4;
    color: ${({ theme }) => theme.colors.textMuted || '#66727d'};
    font-size: 0.7rem;
    font-weight: 600;
`;

const ProgressGrid = styled.dl`
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 0.55rem;
    margin: 0;

    @media (max-width: 1050px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (max-width: 560px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const ProgressItem = styled.div`
    min-width: 0;
    padding: 0.65rem 0.7rem;
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    border-radius: 7px;
    background: ${({ $state }) => $state === 'failed' ? '#fff8f7' : '#fbfcfd'};

    dt {
        overflow: hidden;
        color: ${({ theme }) => theme.colors.textMuted || '#66727d'};
        font-size: 0.72rem;
        line-height: 1.3;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    dd {
        margin: 0.25rem 0 0;
        color: ${({ $state, theme }) => $state === 'failed' ? '#a52828' : theme.colors.textPrimary || '#111827'};
        font-size: 1.05rem;
        font-weight: 700;
        line-height: 1.2;
    }
`;

const ManagedBadge = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 36px;
    padding: 0.45rem 0.7rem;
    border: 1px solid #dbe7f5;
    border-radius: 6px;
    background: #f2f7fd;
    color: #37638f;
    font-size: 0.8rem;
    font-weight: 550;
`;

const TestRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 0.5rem 0 0.75rem;
`;

const TestOk = styled.span`
    font-size: 0.85rem;
    color: #1e7e34;
`;

const SampleBox = styled.div`
    background: rgba(0, 0, 0, 0.03);
    border-radius: 8px;
    padding: 0.75rem;
    margin-bottom: 0.75rem;
`;

const SampleTitle = styled.div`
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.6;
    margin-bottom: 0.3rem;
`;

const FieldList = styled.div`
    font-family: monospace;
    font-size: 0.8rem;
    margin-bottom: 0.75rem;
`;

const SampleTable = styled.table`
    width: 100%;
    font-size: 0.78rem;
    border-collapse: collapse;

    td {
        padding: 0.25rem 0.4rem;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }
`;

const RunRow = styled.div`
    padding: 0.65rem 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const RunHead = styled.div`
    display: flex;
    gap: 1rem;
    align-items: center;
    font-size: 0.85rem;
    flex-wrap: wrap;
`;

const OutcomeList = styled.ul`
    margin: 0.4rem 0 0;
    padding-left: 1.1rem;
    font-size: 0.82rem;
    opacity: 0.8;
`;

const Help = styled.p`
    margin: 0.35rem 0 0;
    font-size: 0.78rem;
    opacity: 0.65;
`;

const MappingBox = styled.div`
    background: rgba(0, 0, 0, 0.03);
    border-radius: 8px;
    padding: 0.75rem;
    margin-bottom: 0.75rem;
`;

const MappingRow = styled.div`
    display: grid;
    grid-template-columns: 150px 1fr;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.5rem;
`;

const MappingLabel = styled.span`
    font-size: 0.85rem;
`;

const Required = styled.span`
    color: #dc3545;
    margin-left: 0.2rem;
`;

const MappingWarning = styled.p`
    margin: 0.6rem 0 0;
    font-size: 0.8rem;
    color: #b02a37;
`;

const ModalActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1.25rem;
`;

export default CatalogSources;
